import { refreshAccessToken } from '../network/network-calls';
import { setCookie, deleteCookie } from './pure-functions';

let refreshTimer = null;
let isRefreshing = false;
let refreshPromise = null;

const TOKEN_REFRESH_BUFFER_MINUTES = 5;

const formatTime = (ms) => {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${seconds}s`;
};

/**
 * Check if the current access token is expiring within the buffer period
 * @param {number} bufferMinutes - Minutes before expiration to consider "expiring soon"
 * @returns {boolean}
 */
export const isTokenExpiringSoon = (bufferMinutes = TOKEN_REFRESH_BUFFER_MINUTES) => {
  const expiration = localStorage.getItem('tokenExpiration');
  if (!expiration) return true;

  const expiresAt = new Date(expiration).getTime();
  const bufferMs = bufferMinutes * 60 * 1000;
  return Date.now() >= (expiresAt - bufferMs);
};

/**
 * Store authentication tokens in localStorage and cookie
 * @param {Object} data - Token response from getUserToken or refreshAccessToken (same API response)
 */
export const storeAuthTokens = (data) => {
  localStorage.setItem('bearer', data.accessToken);
  localStorage.setItem('refreshToken', data.refreshToken);
  localStorage.setItem('tokenExpiration', data.accessTokenExpired);
  setCookie('qkn_bearer', data.accessToken, 1);
};

/**
 * Clear all authentication tokens from storage
 */
export const clearAuthTokens = () => {
  localStorage.removeItem('bearer');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('tokenExpiration');
  deleteCookie('qkn_bearer');
};

/**
 * Perform token refresh using the stored refresh token
 * Handles concurrent refresh requests by returning the same promise
 * @returns {Promise<Object>} - The new token data
 */
export const performTokenRefresh = async () => {
  // If already refreshing, return the existing promise to avoid duplicate calls
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  const storedRefreshToken = localStorage.getItem('refreshToken');
  if (!storedRefreshToken) {
    throw new Error('No refresh token available');
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const { data } = await refreshAccessToken(storedRefreshToken);
      storeAuthTokens(data);
      startTokenRefreshTimer();
      return data;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

/**
 * Start a timer to proactively refresh the token before it expires
 */
export const startTokenRefreshTimer = () => {
  clearTokenRefreshTimer();

  const expiration = localStorage.getItem('tokenExpiration');
  if (!expiration) return;

  const expiresAt = new Date(expiration).getTime();
  const refreshAt = expiresAt - (TOKEN_REFRESH_BUFFER_MINUTES * 60 * 1000);
  const delay = refreshAt - Date.now();

  if (delay > 0) {
    refreshTimer = setTimeout(async () => {
      try {
        await performTokenRefresh();
      } catch (error) {
        // Timer-based refresh failure is silent - the Axios request interceptor will catch it
      }
    }, delay);
  } else if (delay > -(TOKEN_REFRESH_BUFFER_MINUTES * 60 * 1000)) {
    // Token is within the buffer window but not yet expired - refresh immediately
    performTokenRefresh().catch(() => {});
  }
};

/**
 * Clear the token refresh timer
 */
export const clearTokenRefreshTimer = () => {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
};

/**
 * Exchange a refresh token (from URL) for access tokens
 * Used when receiving a refresh token from another application
 * @param {string} refreshToken - The refresh token from URL
 * @returns {Promise<Object>} - The new token data
 */
export const exchangeRefreshToken = async (refreshToken) => {
  const { data } = await refreshAccessToken(refreshToken);
  storeAuthTokens(data);
  startTokenRefreshTimer();
  return data;
};

// Debug feature in non-prod only lives in window.debugTokenState()
export const debugTokenState = () => {
  const expiration = localStorage.getItem('tokenExpiration');
  const hasBearer = !!localStorage.getItem('bearer');
  const hasRefreshToken = !!localStorage.getItem('refreshToken');
  const refreshEnabled = hasBearer && hasRefreshToken && !!expiration;

  let authMode = 'unauthenticated';
  if (refreshEnabled) {
    authMode = 'refresh-token';
  } else if (hasBearer) {
    authMode = 'bearer-only';
  }

  const expiresAt = expiration ? new Date(expiration).getTime() : null;
  const timeUntilExpiry = expiresAt ? expiresAt - Date.now() : null;
  const refreshAt = expiresAt ? expiresAt - (TOKEN_REFRESH_BUFFER_MINUTES * 60 * 1000) : null;
  const timeUntilRefresh = refreshAt ? refreshAt - Date.now() : null;

  const state = {
    authMode,
    hasBearer,
    hasRefreshToken,
    refreshEnabled,
    tokenExpiration: expiration,
    expiresAt: expiresAt ? new Date(expiresAt).toLocaleTimeString() : null,
    timeUntilExpiry: timeUntilExpiry ? formatTime(timeUntilExpiry) : null,
    refreshWillTriggerAt: refreshAt ? new Date(refreshAt).toLocaleTimeString() : null,
    timeUntilRefresh: timeUntilRefresh ? formatTime(timeUntilRefresh) : null,
    timerActive: !!refreshTimer,
    isCurrentlyRefreshing: isRefreshing
  };

  console.table(state);
  return state;
};

// Expose debug helper to window for console access in non-prod
if (typeof window !== 'undefined' && process.env.REACT_APP_ENV !== 'production') {
  window.debugTokenState = debugTokenState;
}
